import { useState, useEffect, useRef } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

interface UseAuthStateReturn {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAuthStateStable: boolean; // New: indicates if auth state has stabilized
}

/**
 * Custom hook that provides stable auth state management
 * Ensures auth state is fully updated before allowing favorite checks
 */
export const useAuthState = (): UseAuthStateReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthStateStable, setIsAuthStateStable] = useState(false);
  const previousUserRef = useRef<User | null>(null);
  const authStateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log('🔄 Auth state changed:', currentUser?.uid || 'null');
      
      // Clear any existing timeout
      if (authStateTimeoutRef.current) {
        clearTimeout(authStateTimeoutRef.current);
      }

      // If user changed (including logout), mark as unstable initially
      if (previousUserRef.current?.uid !== currentUser?.uid) {
        console.log('👤 User changed, marking auth state as unstable');
        setIsAuthStateStable(false);
        
        // Set a small delay to ensure auth state is fully stabilized
        authStateTimeoutRef.current = setTimeout(() => {
          console.log('✅ Auth state stabilized');
          setIsAuthStateStable(true);
        }, 100); // 100ms delay to ensure auth state is fully updated
      } else {
        // Same user, auth state is stable
        setIsAuthStateStable(true);
      }

      setUser(currentUser);
      setLoading(false);
      previousUserRef.current = currentUser;
    });

    return () => {
      unsubscribe();
      if (authStateTimeoutRef.current) {
        clearTimeout(authStateTimeoutRef.current);
      }
    };
  }, []);

  return {
    user,
    loading,
    isAuthenticated: !!user,
    isAuthStateStable,
  };
};
